import { Helmet } from 'react-helmet-async';

const SEO = ({
  title,
  description,
  path,
  type = 'website',
  keywords,
  breadcrumbs,
  faqSchema,
  serviceSchema,
}) => {
  const siteUrl = 'https://laizyones.de';
  const fullTitle = title
    ? `${title} | LAIzyones`
    : 'LAIzyones - KI für den Mittelstand | Prozesse automatisieren & Kosten senken';
  const fullUrl = `${siteUrl}${path}`;
  const ogImage = `${siteUrl}/logo-icon.png`;

  const breadcrumbJsonLd = breadcrumbs
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((crumb, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: crumb.name,
          item: crumb.url ? `${siteUrl}${crumb.url}` : undefined,
        })),
      }
    : null;

  const faqJsonLd =
    faqSchema && faqSchema.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqSchema.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        }
      : null;

  const serviceJsonLd = serviceSchema
    ? {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: serviceSchema.name,
        description: serviceSchema.description,
        url: fullUrl,
        provider: {
          '@type': 'Organization',
          name: 'LAIzyones',
          url: siteUrl,
        },
        areaServed: [
          { '@type': 'City', name: 'Magdeburg' },
          { '@type': 'City', name: 'Braunschweig' },
          { '@type': 'Country', name: 'Deutschland' },
        ],
        ...(serviceSchema.offers
          ? { offers: { '@type': 'Offer', description: serviceSchema.offers } }
          : {}),
      }
    : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullUrl} />
      <meta name="robots" content="index, follow" />
      <link rel="alternate" hrefLang="de" href={fullUrl} />
      <link rel="alternate" hrefLang="x-default" href={fullUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content="de_DE" />
      <meta property="og:site_name" content="LAIzyones" />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1024" />
      <meta property="og:image:height" content="1024" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Geo Tags for Local SEO */}
      <meta name="geo.region" content="DE-ST" />
      <meta name="geo.placename" content="Magdeburg" />

      {/* Breadcrumb JSON-LD */}
      {breadcrumbJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd)}
        </script>
      )}

      {/* FAQ JSON-LD */}
      {faqJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(faqJsonLd)}
        </script>
      )}

      {/* Service JSON-LD */}
      {serviceJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(serviceJsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
